import { useCallback } from "react";

import { designate } from "../../components/attention";
import { t } from "../../i18n/i18n";
import { useUi } from "../../state/ui";
import { isConflict } from "../history";
import type { OrientationView } from "./orientationView";
import { useManoeuvreWrites } from "./useManoeuvreWrites";
import { useOrientation } from "./useOrientation";

/** Cibles désignées du mode orientation (`data-attention`). */
export const ORIENTATION_ATTENTION = {
  rose: "orientation-rose",
  calloutAction: "orientation-callout-action",
  apply: "orientation-apply",
} as const;

export function attention(name: string): string {
  return `[data-attention="${name}"]`;
}

/** Verdict d'« Appliquer » (fonction pure) : sens inchangé (la rose est désignée), impossible (l'action du Callout est
 *  désignée), ou écriture. */
export function applyVerdict(view: Pick<OrientationView, "unchanged" | "reason"> | null): "none" | "unchanged" | "impossible" | "apply" {
  if (!view) return "none";
  if (view.unchanged) return "unchanged";
  if (view.reason) return "impossible";
  return "apply";
}

/** « Appliquer » du mode orientation (panneau, bandeau, Entrée) : sens inchangé ou impossible → l'action attendue est
 *  désignée (rose, action du Callout) ; sinon l'écriture part (`setOrientation`), le mode reste ouvert jusqu'à la
 *  réponse (l'aperçu reste affiché, B28) puis se ferme ; refus (422) : le mode reste ouvert ; conflit : fermé. */
export function useApplyOrientation(): { apply: (view: OrientationView | null) => void; busy: boolean } {
  const writes = useManoeuvreWrites();
  const notify = useUi((s) => s.notify);
  const pending = useOrientation((s) => s.pending);
  const { setOrientation } = writes;
  const apply = useCallback(
    (view: OrientationView | null) => {
      const store = useOrientation.getState();
      const verdict = applyVerdict(view);
      if (!view || verdict === "none" || store.pending || !store.open) return;
      if (verdict === "unchanged") {
        notify({ text: t("Choisissez un autre sens"), tone: "info" });
        designate(attention(ORIENTATION_ATTENTION.rose), "ring");
        return;
      }
      if (verdict === "impossible") {
        notify({ text: t("Ce sens ne tient pas dans le camp"), tone: "warn" });
        designate(attention(ORIENTATION_ATTENTION.calloutAction), "ring");
        return;
      }
      store.setPending(true);
      setOrientation
        .mutateAsync({ bearing: view.target, graphics: store.graphics && view.graphicsTotal > 0 })
        .then(() => useOrientation.getState().close())
        .catch((error: unknown) => {
          if (isConflict(error)) useOrientation.getState().close();
          else useOrientation.getState().setPending(false);
        });
    },
    [notify, setOrientation],
  );
  return { apply, busy: pending || writes.busy };
}
